import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Checkbox } from '../ui/checkbox';
import { CalendarRange, ChevronDown, ChevronRight, FileStack, Link2, Loader2, RefreshCw } from 'lucide-react';
import { MonthlyBatchDialog } from './MonthlyBatchDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const eur = (v) => `€${(Number(v) || 0).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('es-ES') : '—');
const daysAgo = (iso) => (iso ? Math.floor((Date.now() - new Date(iso).getTime()) / 86400000) : null);

const CustomerRow = ({ g, onInvoice, isEs }) => {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(() => new Set(g.orders.map((o) => o.id)));
  useEffect(() => { setSelected(new Set(g.orders.map((o) => o.id))); }, [g]);
  const chosen = g.orders.filter((o) => selected.has(o.id));
  const chosenTotal = chosen.reduce((s, o) => s + (Number(o.pvp) || 0), 0);
  const age = daysAgo(g.oldest_date);
  const key = g.customer_id || g.customer_name || 'sin-cliente';
  const toggle = (id) => setSelected((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  return (
    <div className="border-b last:border-b-0" data-testid={`pending-customer-${key}`}>
      <div className="flex flex-wrap items-center gap-3 px-4 py-3 hover:bg-slate-50">
        <button type="button" onClick={() => setOpen(!open)} className="flex items-center gap-2 flex-1 min-w-[200px] text-left" data-testid={`pending-toggle-${key}`}>
          {open ? <ChevronDown className="h-4 w-4 text-slate-500" /> : <ChevronRight className="h-4 w-4 text-slate-500" />}
          <div>
            <p className="font-semibold text-slate-800">{g.company || g.customer_name || (isEs ? '(sin cliente)' : '(no customer)')}</p>
            <p className="text-xs text-slate-500">
              {g.nif || (isEs ? 'Sin NIF' : 'No VAT ID')} · {g.country || (g.suggested_iva === 0 ? (isEs ? 'UE (por NIF)' : 'EU (by VAT ID)') : 'España')} · IVA {g.suggested_iva}%
            </p>
          </div>
        </button>
        <div className="text-sm text-slate-600 w-28 text-right">{g.count} {isEs ? (g.count === 1 ? 'pedido' : 'pedidos') : (g.count === 1 ? 'order' : 'orders')}</div>
        <div className="text-sm font-bold text-emerald-700 w-28 text-right" data-testid={`pending-total-${key}`}>{eur(g.total_pvp)}</div>
        <div className={`text-xs w-32 text-right ${age > 30 ? 'text-rose-600 font-semibold' : 'text-slate-500'}`}>
          {isEs ? 'Más antiguo' : 'Oldest'}: {age !== null ? `${age} d` : '—'}
        </div>
        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" disabled={!chosen.length}
          onClick={() => onInvoice(chosen, g)} data-testid={`pending-invoice-${key}`}>
          <FileStack className="h-4 w-4 mr-1" />
          {chosen.length === g.count ? (isEs ? 'Facturar todo' : 'Invoice all') : (isEs ? `Facturar ${chosen.length}` : `Invoice ${chosen.length}`)}
        </Button>
      </div>
      {open && (
        <div className="bg-slate-50/60 px-4 pb-3">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-slate-500">
                <th className="w-8 py-1.5 text-left">
                  <Checkbox checked={selected.size === g.count} onCheckedChange={(v) => setSelected(v ? new Set(g.orders.map((o) => o.id)) : new Set())} data-testid={`pending-select-all-${key}`} />
                </th>
                <th className="text-left">{isEs ? 'Pedido' : 'Order'}</th>
                <th className="text-left">{isEs ? 'Medio' : 'Site'}</th>
                <th className="text-left">{isEs ? 'URL publicada' : 'Published URL'}</th>
                <th className="text-left">{isEs ? 'Fecha' : 'Date'}</th>
                <th className="text-right">PVP</th>
              </tr>
            </thead>
            <tbody>
              {g.orders.map((o) => (
                <tr key={o.id} className="border-t border-slate-200">
                  <td className="py-1.5"><Checkbox checked={selected.has(o.id)} onCheckedChange={() => toggle(o.id)} data-testid={`pending-order-check-${o.order_number}`} /></td>
                  <td className="font-semibold text-indigo-600">#{o.order_number}</td>
                  <td className="truncate max-w-[220px]">{o.product_url}</td>
                  <td className="truncate max-w-[260px]">
                    {o.published_url ? <a href={o.published_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{o.published_url}</a> : '—'}
                  </td>
                  <td>{fmtDate(o.order_date)}</td>
                  <td className="text-right font-medium text-emerald-700">{eur(o.pvp)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {chosen.length !== g.count && (
            <p className="text-xs text-slate-500 mt-2">{isEs ? 'Seleccionados' : 'Selected'}: {chosen.length} · {eur(chosenTotal)}</p>
          )}
        </div>
      )}
    </div>
  );
};

export const PendingInvoicingPanel = ({ onInvoice, refreshKey, language, onBatchDone }) => {
  const isEs = language === 'es';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [batchOpen, setBatchOpen] = useState(false);
  const [linking, setLinking] = useState(false);
  const linkOrders = async () => {
    setLinking(true);
    try {
      const r = (await axios.post(`${API}/admin/orders/link-customers`)).data;
      const rest = r.unmatched.slice(0, 4).map((u) => `${u.customer} (${u.orders})`).join(', ');
      toast.success(`${r.linked} pedidos vinculados a su cliente`, {
        description: r.unmatched.length ? `Sin ficha de cliente: ${rest}${r.unmatched.length > 4 ? '…' : ''}. Créala en Clientes con el mismo email.` : undefined,
        duration: 9000,
      });
      load();
    } catch { toast.error('Error al vincular pedidos'); } finally { setLinking(false); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/income-invoices/pending-by-customer`);
      setData(r.data);
    } catch {
      toast.error(isEs ? 'Error al cargar pendientes de facturar' : 'Error loading pending invoicing');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Card className="overflow-hidden border-emerald-200" data-testid="pending-invoicing-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 bg-emerald-50 border-b border-emerald-200">
        <button type="button" onClick={() => setCollapsed(!collapsed)} className="flex items-center gap-2 text-left" data-testid="pending-invoicing-collapse">
          {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          <div>
            <p className="font-bold text-emerald-900">{isEs ? 'Pendiente de facturar' : 'Pending invoicing'}</p>
            <p className="text-xs text-emerald-700">{isEs ? 'Pedidos publicados sin factura, agrupados por cliente' : 'Published orders without invoice, grouped by customer'}</p>
          </div>
        </button>
        <div className="flex items-center gap-4">
          {data && (
            <p className="text-sm text-emerald-900" data-testid="pending-invoicing-summary">
              <b>{data.total_orders}</b> {isEs ? 'pedidos' : 'orders'} · <b>{eur(data.total_pvp)}</b> · {data.customers.length} {isEs ? 'clientes' : 'customers'}
            </p>
          )}
          <Button variant="outline" size="sm" onClick={linkOrders} disabled={linking} title={isEs ? 'Une los pedidos antiguos a la ficha del cliente por email o nombre' : 'Link old orders to customer records'} data-testid="link-orders-customers-btn">
            {linking ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Link2 className="h-4 w-4 mr-1" />}{isEs ? 'Vincular pedidos a clientes' : 'Link orders to customers'}
          </Button>
          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setBatchOpen(true)} disabled={!data?.customers?.length} data-testid="monthly-batch-open-btn">
            <CalendarRange className="h-4 w-4 mr-1" />{isEs ? 'Facturas del mes' : 'Monthly invoices'}
          </Button>
          <Button variant="ghost" size="sm" onClick={load} disabled={loading} data-testid="pending-invoicing-refresh">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          </Button>
        </div>
      </div>
      {!collapsed && (
        <div className="max-h-[480px] overflow-y-auto">
          {data && data.customers.length === 0 && (
            <p className="text-sm text-slate-500 p-4">{isEs ? 'No hay pedidos publicados pendientes de facturar.' : 'No published orders pending invoicing.'}</p>
          )}
          {data?.customers.map((g) => (
            <CustomerRow key={g.customer_id || g.customer_name || 'sin-cliente'} g={g} onInvoice={onInvoice} isEs={isEs} />
          ))}
        </div>
      )}
      <MonthlyBatchDialog open={batchOpen} onOpenChange={setBatchOpen} groups={data?.customers || []} onDone={() => { load(); onBatchDone && onBatchDone(); }} />
    </Card>
  );
};

export default PendingInvoicingPanel;
